<!-- Taken from https://vuejsdevelopers.com/2017/10/23/vue-js-tree-menu-recursive-components/ -->
<template>
  <div v-if="!masked" class="tree-menu">

    <!-- Item -->
    <div class="tree-menu-item"
         v-bind:class="{ 'selected': selected, 'hidden': hasChildren && flat }"
         :style="indent"
         @mousedown="select()">

      <!-- Badges -->
      <EvaluationBadge ref="badge" :value="value" :outline="hasChildren" />
      <fa v-if="!hasChildren" class="badge-spaced" v-bind:class="{ 'text-muted': !data.comment }" :icon="[data.comment ? 'fas' : 'far', 'comment-alt']" />

      <!-- Popover -->
      <b-popover v-if="!hasChildren" ref="popover" :target="badge">
        <button type="button" class="btn btn-sm evaluation-badge-0" @click="setValue(0)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '0') : ''">0</button>
        <button type="button" class="btn btn-sm evaluation-badge-1" @click="setValue(1)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '1') : ''">1</button>
        <button type="button" class="btn btn-sm evaluation-badge-2" @click="setValue(2)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '2') : ''">2</button>
        <button type="button" class="btn btn-sm evaluation-badge-3" @click="setValue(3)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '3') : ''">3</button>
        <button type="button" class="btn btn-sm evaluation-badge-4" @click="setValue(4)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '4') : ''">4</button>
        <button type="button" class="btn btn-sm evaluation-badge-5" @click="setValue(5)" data-toggle="tooltip" data-placement="top" :title="tips ? $t(tips + '5') : ''">X</button>
      </b-popover>

      <!-- Numbering -->
      <span v-if="depth > 0">
        {{ getNumbering() }}
      </span>

      <!-- Title -->
      {{ $t(name) }}

      <!-- Arrow (if has children) -->
      <fa v-if="hasChildren" :icon="isCollapsed ? 'angle-down' : 'angle-right'" fixed-width/>

    </div>

    <!-- Children -->
    <tree-view-item v-if="hasChildren && isCollapsed && !masked"
                    v-for="(child, id) in children"
                    :data="child"
                    :parent="this"
                    :numbering="getNumbering()"
                    :depth="depth + 1"
                    :flat="flat"
                    :index="id"
                    :key="child.name"
                    :priority="priority"
                    :tips="tips"
                    :titles="titles"
                    :defaultValue="defaultValue"
                    ref="children"
                    v-on:select="notifySelection()"
                    @updated="$emit('updated')">
    </tree-view-item>

  </div>
</template>

<style scoped>
.badge-spaced {
  margin: 0 0.3em;
}
.tree-menu-item {
  user-select: none;
}
</style>

<script>
import Vue from 'vue';

export default {
  name: 'tree-view-item',

  props: [
    'collapsed',  // Show children or not.
    'data',       // Data tree.
    'depth',      // Depth we're working on.
    'flat',       // Flatten the tree.
    'focus',      // Focussed node (others are hidden).
    'index',      // Index of this node from parent.
    'numbering',  // Real numbering (includes root numbering).
    'priority',   // Priority => false for minimum, true for maximum.
    'tips',       // Prefix for the tips translation.
    'titles',     // Prefix for the title translation.
    'defaultValue' // default value to be filled
  ],

  computed: {
    children () {
      if (this.focus != null && this.focus.children != null) {
        return this.focus.children;
      }

      return this.data.children;
    },

    gap () {
      return this.flat ? 0 : this.depth;
    },

    hasChildren () {
      return this.data.children.length > 0;
    },

    indent () {
      return {
        paddingLeft: `${this.gap * 20 + 10}px`
      }
      //return { transform: `translate(${this.depth * 20}px)` }
    },

    masked () {
      if (this.data.masked) {
        return this.data.masked;
      }

      return false;
    },

    maximum () {
      return 4;
    },

    name () {
      if (this.depth > 0) {
        return this.titles + this.data.id;
      }

      return this.titles.split('.')[1];
    },

    value () {
      if ('value' in this.data) {
        return this.data.value;
      }
      return this.defaultValue;
    },

    valuesMenu () {
      return this.$refs.popover;
    }
  },


  data: () => ({
    badge: this,
    choices: ['0', '1', '2', '3', '4', 'X'],
    isCollapsed: this.collapsed || false,
    selected: false,
  }),

  methods: {
    /**
     * Returns visible nodes.
     *
     * @param List List of previous nodes.
     * @return List of visible nodes.
     */
    getMask (list) {
      // Don't work on leaves.
      if (!this.hasChildren) {
        return list;
      }

      list.push(this.isCollapsed ? 1 : 0);

      // Work on children if shown.
      if (this.isCollapsed) {
        let children = this.$refs.children;

        for (let i = 0; i < children.length; i++) {
          list = children[i].getMask(list);
        }
      }

      return list;
    },

    /**
     * Returns numbering of node.
     *
     * @return Current numbering
     */
    getNumbering () {
      if (this.depth == 0) {
        return this.numbering;
      }

      // In prop : "`${numbering}.${(index + 1)}.`"
      return this.numbering + (this.index + 1) + '.';
    },

    /**
     * Notifies parent that a selection has been made.
     */
    notifySelection (item) {
      this.$parent.notifySelection(item);
    },

    /**
     * Notify that a selection has been made from this component.
     */
    select () {
      this.toggleChildren();
      this.notifySelection(this);
    },

    /**
     * Bubble up value to parent.
     *
     * @param Value Value to bubble up.
     */
    setParentValue (value) {
      // Bubble up to root.
      if (this.depth > 0) {
        this.$parent.setParentValue(value);
      } else {
        // If root, update values once.
        this.updateValues(this.data, value);
      }

      // Update value of this node.
      Vue.set(this.data, 'value', this.value);

      // Hide popover if necessary (leaf nodes only).
      if (!this.hasChildren) {
        this.valuesMenu.$emit('close');
      }
    },

    /**
     * Set selection in TreeView.
     * If given item is this instance, mark it as selected.
     *
     * @param Item TreeView node selected.
     */
    setSelection (item) {
      // Should we put this instance as selected ?
      this.selected = item == this;

      // IF has children, propagate selection.
      if (this.$refs.children) {
        this.$refs.children.forEach((child) => {
          child.setSelection(item);
        });
      // Else, close popover if necessary (only for children has popovers).
    } else if (this.valuesMenu && !this.selected) {
        this.valuesMenu.$emit('close');
      }
    },

    /**
     * Set value of current node.
     *
     * @param Value Value to set.
     */
    setValue (value) {
      // Set value of this node.
      // NOTE : As this.data isn't responsive, we have to change it by Vue.set()
      //        in order to notify its change.
      Vue.set(this.data, 'value', value);

      // Bubble up value to parents.
      this.setParentValue(value);

      this.$emit('updated');
    },

    /**
     * Toggle children.
     */
    toggleChildren () {
      this.isCollapsed = !this.isCollapsed;
    },

    /**
     * Update values of tree.
     *
     * @param Data Datas tree.
     * @return Minimum value in the tree.
     */
    updateValues (data) {
      var length = Object.keys(data.children).length;

      // If node has children...
      if (length > 0) {
        var value = this.defaultValue;

        // Look for minimum value in children.
        for (var i = 0; i < length; i++) {
          var v = this.updateValues(data.children[i]);
          // Check for maximum or minimum according to priority.
          //
          // NOTE : For a better optimization, split this condition in two
          //        loops. The application will have less conditions to check
          //        but the code will be ugly.
          // NOTE : I did some tests and performances between the two solutions
          //        are the same.
          var priorityUpdate = (v > value && v <= this.maximum) || value > this.maximum;
          var nonPriorityUpdate = v < value;
          if ((this.priority && priorityUpdate) || (!this.priority && nonPriorityUpdate)) {
            value = v;
          }
        }

        // Set current node value.
        Vue.set(data, 'value', value);

        return value;
      } else { // Node is leaf.
        if ('value' in data) {
          return data.value;
        } else {
          Vue.set(data, 'value', this.defaultValue);
          return this.defaultValue;
        }
      }
    },
  },

  mounted () {
    // Link badge after creation.
    this.badge = this.$refs.badge;

    // Collapse children if necessary.
    this.isCollapsed = this.collapsed || this.flat;
  }
}
</script>



// WEBPACK FOOTER //
// ../../../../resources/js/components/TreeViewItem.vue